import React, { useState, useEffect } from 'react';
import {
  X, Copy, Check, Share2, Mail, MessageSquare,
  Twitter, Facebook, Linkedin, ExternalLink
} from 'lucide-react';
import './ShareModal.css';

const WhatsAppIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={{ color: '#25D366', flexShrink: 0 }}>
    <path d="M17.472 14.382c-.301-.15-1.782-.88-2.059-.98-.276-.1-.477-.15-.678.15-.2.3-.778.98-.954 1.18-.175.2-.351.225-.652.075-.301-.15-1.27-.468-2.42-1.494-.894-.798-1.498-1.784-1.674-2.085-.175-.3-.019-.462.132-.612.136-.135.301-.351.452-.527.15-.175.2-.3.301-.5.1-.2.05-.376-.025-.526-.075-.15-.678-1.636-.929-2.24-.244-.588-.492-.508-.677-.518l-.578-.01c-.2 0-.527.075-.803.376-.276.3-1.054 1.03-1.054 2.512s1.079 2.913 1.23 3.114c.15.2 2.124 3.244 5.145 4.549.718.31 1.28.496 1.717.635.722.23 1.378.198 1.898.12.579-.087 1.782-.728 2.033-1.431.25-.704.25-1.306.175-1.431-.075-.126-.276-.201-.577-.351zm-5.42 6.788c-1.85 0-3.665-.498-5.263-1.444l-.377-.224-3.914 1.026 1.045-3.814-.246-.391a10.87 10.87 0 0 1-1.666-5.839c0-6.02 4.898-10.917 10.925-10.917 2.917 0 5.66 1.137 7.723 3.2 2.062 2.064 3.197 4.808 3.197 7.728 0 6.023-4.898 10.92-10.924 10.92zm9.28-17.008C18.892 1.71 15.602.5 12.052.5 5.736.5.586 5.65.586 11.966c0 2.018.528 3.99 1.53 5.728L0 24l6.477-1.7c1.674.912 3.559 1.393 5.485 1.393 6.315 0 11.465-5.15 11.465-11.466 0-3.064-1.194-5.945-3.365-8.115z"/>
  </svg>
);

export default function ShareModal({
  isOpen,
  onClose,
  title = 'Share Test or Panel',
  testName = 'Diagnostic Panel',
  testId = '',
  testSlug = '',
  shareUrl = '',
  category = ''
}) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const origBody = document.body.style.overflow;
    const origDoc = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = origBody;
      document.documentElement.style.overflow = origDoc;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Build clean canonical deep link (stripping leading slashes and path prefixes)
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://musbdiagnostics.com';
  const cleanSlug = String(testSlug || '')
    .replace(/^\/lab-tests\//, '')
    .replace(/^\//, '')
    .trim();
  const cleanId = String(testId || '').trim();
  const paramKey = encodeURIComponent(cleanSlug || cleanId || (testName ? testName.toLowerCase().replace(/[^a-z0-9]+/g, '-') : 'test'));
  const fallbackUrl = `${origin}/tests?test=${paramKey}`;
  const finalUrl = shareUrl || fallbackUrl;

  const shareText = `Explore "${testName}" on MusB Diagnostics — Certified Laboratory Diagnostics & Mobile Blood Collection:`;

  const handleCopyLink = async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(finalUrl);
      } else {
        const input = document.createElement('input');
        input.value = finalUrl;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleNativeShare = async () => {
    if (navigator?.share) {
      try {
        await navigator.share({
          title: testName,
          text: shareText,
          url: finalUrl
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Native share error:', err);
        }
      }
    } else {
      handleCopyLink();
    }
  };

  const encodedUrl = encodeURIComponent(finalUrl);
  const encodedText = encodeURIComponent(`${shareText} ${finalUrl}`);
  const encodedSubject = encodeURIComponent(`Recommended Diagnostic Panel: ${testName}`);
  const encodedEmailBody = encodeURIComponent(`Hello,\n\nI wanted to share this diagnostic test from MusB Diagnostics with you:\n\n${testName}\n${finalUrl}\n\nCLIA Certified • COLA Accredited • HIPAA Compliant.`);

  return (
    <div className="share-modal-overlay" onClick={onClose}>
      <div className="share-modal-card" onClick={(e) => e.stopPropagation()}>
        
        {/* Header */}
        <div className="share-modal-header">
          <div className="share-modal-title-wrap">
            <div className="share-icon-pill">
              <Share2 size={18} />
            </div>
            <div>
              <h3 className="share-modal-title">{title}</h3>
              <p className="share-modal-subtitle">Share link with patients, clinicians, or family</p>
            </div>
          </div>
          <button type="button" className="share-close-btn" onClick={onClose} aria-label="Close share dialog">
            <X size={18} />
          </button>
        </div>

        {/* Item Preview Card */}
        <div className="share-item-preview">
          <div className="share-item-name">{testName}</div>
          {category && <span className="share-item-category">{category}</span>}
          <div className="share-item-url-hint">{finalUrl}</div>
        </div>

        {/* Copy Link Field */}
        <div className="share-link-box">
          <input
            type="text"
            readOnly
            value={finalUrl}
            className="share-link-input"
            onClick={(e) => e.target.select()}
          />
          <button
            type="button"
            className={`share-copy-btn ${copied ? 'copied' : ''}`}
            onClick={handleCopyLink}
          >
            {copied ? (
              <>
                <Check size={16} /> Copied!
              </>
            ) : (
              <>
                <Copy size={16} /> Copy
              </>
            )}
          </button>
        </div>

        {/* Social & Direct Channels */}
        <div className="share-channels-title">Share directly via:</div>
        <div className="share-channels-grid">
          
          {/* Native Web Share API (Mobile / Tablet) */}
          {typeof navigator !== 'undefined' && navigator.share && (
            <button
              type="button"
              className="share-channel-btn share-native"
              onClick={handleNativeShare}
            >
              <ExternalLink size={17} />
              <span>More Options</span>
            </button>
          )}

          {/* WhatsApp */}
          <a
            href={`https://api.whatsapp.com/send?text=${encodedText}`}
            target="_blank"
            rel="noopener noreferrer"
            className="share-channel-btn share-whatsapp"
          >
            <WhatsAppIcon size={18} />
            <span>WhatsApp</span>
          </a>

          {/* SMS */}
          <a
            href={`sms:?&body=${encodedText}`}
            className="share-channel-btn share-sms"
          >
            <MessageSquare size={17} style={{ color: '#38bdf8' }} />
            <span>SMS / Text</span>
          </a>

          {/* Email */}
          <a
            href={`mailto:?subject=${encodedSubject}&body=${encodedEmailBody}`}
            className="share-channel-btn share-email"
          >
            <Mail size={17} style={{ color: '#ea580c' }} />
            <span>Email</span>
          </a>

          {/* Twitter / X */}
          <a
            href={`https://twitter.com/intent/tweet?text=${encodedText}`}
            target="_blank"
            rel="noopener noreferrer"
            className="share-channel-btn share-twitter"
          >
            <Twitter size={17} style={{ color: '#1d9bf0' }} />
            <span>X (Twitter)</span>
          </a>

          {/* Facebook */}
          <a
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
            target="_blank"
            rel="noopener noreferrer"
            className="share-channel-btn share-facebook"
          >
            <Facebook size={17} style={{ color: '#1877f2' }} />
            <span>Facebook</span>
          </a>

          {/* LinkedIn */}
          <a
            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}
            target="_blank"
            rel="noopener noreferrer"
            className="share-channel-btn share-linkedin"
          >
            <Linkedin size={17} style={{ color: '#0a66c2' }} />
            <span>LinkedIn</span>
          </a>
        </div>

        {/* Footer Guarantee */}
        <div className="share-modal-footer">
          Clicking the link will automatically open the full details and booking requisition for this test.
        </div>

      </div>
    </div>
  );
}
